import { SafetyLink } from '../components/AccountSafety';
import React, { useContext, useState, useEffect, useCallback } from 'react';
import { UserContext } from '../contexts/UserContext';
import { getPageContainerStyles, getSearchFormStyles } from '../utils/styleUtils';
import styles from './SettingsPage.module.css';
import GoogleCalendarSync from '../components/GoogleCalendarSync';
import { useLocation, useNavigate } from 'react-router-dom';
import { getThemeForPage } from '../styles/themes';
import { THEME_TYPES, THEME_LABELS } from '../utils/theme';
import { PWA_INSTALL_ROUTE } from '../constants/pwaInstallConfig';
import { getPwaInstallText } from '../constants/pwaInstallText';

// SaveIcon 컴포넌트
const SaveIcon = ({ activeColor = '#00ff00', inactiveColor = '#f0f0f0', hoverColor = '#ffff00' }) => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={styles.saveIcon}
    style={{
      '--normal-color': inactiveColor,
      '--hover-color': hoverColor
    }}
  >
    <path
      d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"
      stroke="var(--normal-color)"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <polyline
      points="17,21 17,13 7,13 7,21"
      stroke="var(--normal-color)"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <polyline
      points="7,3 7,8 15,8"
      stroke="var(--normal-color)"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TEXT = {
  title: {
    ko: '설정',
    en: 'Settings',
    fr: 'Réglages'
  },
  language: {
    ko: '언어 설정',
    en: 'Language Settings',
    fr: 'Paramètres de langue'
  },
  save: {
    ko: '저장',
    en: 'Save',
    fr: 'Enregistrer'
  },
  back: {
    ko: '뒤로',
    en: 'Back',
    fr: 'Retour'
  },
  // 추가 다국어 텍스트
  loading: {
    ko: '사용자 정보를 불러오는 중...',
    en: 'Loading user info...',
    fr: 'Chargement des informations utilisateur...'
  },
  currentSettings: {
    ko: '현재 설정',
    en: 'Current Settings',
    fr: 'Paramètres actuels'
  },
  languageLabel: {
    ko: '언어',
    en: 'Language',
    fr: 'Langue'
  },
  userLabel: {
    ko: '사용자',
    en: 'User',
    fr: 'Utilisateur'
  },
  levelLabel: {
    ko: '레벨',
    en: 'Level',
    fr: 'Niveau'
  },
  unknown: {
    ko: '알 수 없음',
    en: 'Unknown',
    fr: 'Inconnu'
  },
  // Google Calendar 관련 메시지
  googleCalendarSyncSuccess: {
    ko: 'Google Calendar 연동이 완료되었습니다!',
    en: 'Google Calendar sync completed!',
    fr: 'Synchronisation Google Calendar terminée !'
  },
  googleCalendarSyncFailed: {
    ko: 'Google Calendar 연동에 실패했습니다: ',
    en: 'Google Calendar sync failed: ',
    fr: 'Échec de la synchronisation Google Calendar : '
  },
  unknownError: {
    ko: '알 수 없는 오류',
    en: 'Unknown error',
    fr: 'Erreur inconnue'
  },
  // 언어 설정 관련 메시지
  languageSaved: {
    ko: '언어 설정이 저장되었습니다.',
    en: 'Language settings saved.',
    fr: 'Paramètres de langue enregistrés.'
  },
  languageSaveFailed: {
    ko: '언어 설정 저장에 실패했습니다.',
    en: 'Failed to save language settings.',
    fr: 'Échec de l\'enregistrement des paramètres de langue.'
  },
  languageSaveError: {
    ko: '언어 설정 저장 중 오류가 발생했습니다.',
    en: 'Error occurred while saving language settings.',
    fr: 'Erreur lors de l\'enregistrement des paramètres de langue.'
  },
  themeLabel: {
    ko: '테마',
    en: 'Theme',
    fr: 'Thème'
  }
};

const LANGUAGE_OPTIONS = [
  { value: 'ko', label: '한국어' },
  { value: 'en', label: 'English' },
  { value: 'fr', label: 'Français' }
];

export default function SettingsPage() {
  const { userInfo, updateLanguage, userTheme, updateTheme } = useContext(UserContext);
  const lang = userInfo?.preferred_language || 'ko';
  const location = useLocation();
  const navigate = useNavigate();
  const installText = getPwaInstallText(lang);
  const [selectedLanguage, setSelectedLanguage] = useState(userInfo?.preferred_language || 'ko');
  const [selectedTheme, setSelectedTheme] = useState(userTheme || THEME_TYPES.MODERN);
  const [isSaving, setIsSaving] = useState(false);

  // 공통 스타일 가져오기
  const pageContainerStyles = getPageContainerStyles();
  const searchStyles = getSearchFormStyles();

  // 테마 설정
  const currentTheme = getThemeForPage(location.pathname);

  // 사용자가 선택한 테마 확인
  const userSelectedTheme = localStorage.getItem('user_theme') || 'modern';
  const isDosTheme = userSelectedTheme === 'dos';
  const settingsCardClass = `${styles.settingsCard} ${isDosTheme ? styles.dosSettingsCard : styles.modernSettingsCard}`;

  useEffect(() => {
    setSelectedLanguage(userInfo?.preferred_language || 'ko');
  }, [userInfo]);

  const handleGoogleAuthCallback = useCallback(async (code) => {
    try {
      const response = await fetch('/api/calendar/google-auth-callback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code })
      });

      if (response.ok) {
        alert(TEXT.googleCalendarSyncSuccess[lang]);
        // URL에서 파라미터 제거
        window.history.replaceState({}, document.title, window.location.pathname);
      } else {
        const data = await response.json();
        alert(TEXT.googleCalendarSyncFailed[lang] + (data.message || TEXT.unknownError[lang]));
      }
    } catch (error) {
      console.error('Google OAuth 콜백 처리 실패:', error);
      alert(TEXT.googleCalendarSyncFailed[lang] + TEXT.unknownError[lang]);
    }
  }, [lang]);

  // DOS 테마에 맞게 PWA 노치 여백 배경색 설정
  useEffect(() => {
    // theme-color 메타 태그 업데이트
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      themeColorMeta.setAttribute('content', currentTheme.backgroundColor);
    }

    // body 배경색도 테마로 변경 (PWA 노치 여백 포함)
    const originalBodyBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = currentTheme.backgroundColor;

    // 정리 함수: 페이지를 벗어날 때 기본값으로 복원
    return () => {
      if (themeColorMeta) {
        themeColorMeta.setAttribute('content', '#fffdf8'); // 기본 검은색으로 복원
      }
      document.body.style.backgroundColor = originalBodyBg; // 원래 배경색으로 복원
    };
  }, [currentTheme.backgroundColor]);

  // Google OAuth 콜백 처리
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const googleAuth = urlParams.get('google_auth');
    const code = urlParams.get('code');

    if (googleAuth === 'success' && code) {
      // 인증 코드로 토큰 저장
      handleGoogleAuthCallback(code);
    } else if (googleAuth === 'error') {
      // 에러 처리
      const message = urlParams.get('message');
      alert(TEXT.googleCalendarSyncFailed[lang] + (message || TEXT.unknownError[lang]));
    }
  }, [handleGoogleAuthCallback, lang, location.search]);

  const handleLanguageChange = (event) => {
    setSelectedLanguage(event.target.value);
  };

  const handleThemeChange = (event) => {
    setSelectedTheme(event.target.value);
  };

  const handleSave = async () => {
    if (!updateLanguage || !updateTheme) {
      console.error('update functions not available');
      return;
    }

    setIsSaving(true);

    try {
      // 언어 저장
      const languageSuccess = await updateLanguage(selectedLanguage);

      // 테마 저장
      updateTheme(selectedTheme);

      if (languageSuccess) {
        alert(TEXT.languageSaved[lang]);
      } else {
        alert(TEXT.languageSaveFailed[lang]);
      }
    } catch (error) {
      console.error('Settings update error:', error);
      alert(TEXT.languageSaveError[lang]);
    } finally {
      setIsSaving(false);
    }
  };



  if (process.env.NODE_ENV === 'development') {
    console.log('SettingsPage render:', { userInfo: userInfo ? '[PRESENT]' : '[MISSING]', lang, selectedLanguage });
  }

  if (!userInfo) {
    return (
      <div className={styles.container}>
        <div>{TEXT.loading[lang]}</div>
      </div>
    );
  }

  return (
    <div
      className={pageContainerStyles.pageContainer}
      style={{
        overflow: 'auto',
        height: '100%',
        backgroundColor: currentTheme.backgroundColor,
        scrollbarWidth: 'none', // Firefox
        msOverflowStyle: 'none', // IE/Edge
        '--active-color': currentTheme.activeColor,
        '--inactive-color': currentTheme.inactiveColor,
        '--background-color': currentTheme.backgroundColor,
        '--border-color': currentTheme.borderColor
      }}
    >
      {/* 페이지 타이틀 */}
      <h1 className={pageContainerStyles.pageTitle}>
        {TEXT.title[lang]}
      </h1>

      {/* 언어 설정 섹션 */}
      <div className={settingsCardClass}>
        <h2 className={styles.sectionTitle} style={{ margin: '0 0 15px 0' }}>
          {TEXT.language[lang]}
        </h2>

        <div className={searchStyles.searchForm}>
          <select
            className={styles.languageSelect}
            value={selectedLanguage}
            onChange={handleLanguageChange}
            style={{ border: `1px dashed ${isDosTheme ? '#00ff00' : '#ccc'} !important` }}
          >
            {LANGUAGE_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <button
            onClick={handleSave}
            disabled={isSaving || selectedLanguage === (userInfo?.preferred_language || 'ko')}
            className={styles.saveButton}
            title={TEXT.save[lang]}
            style={{ border: 'none' }}
          >
            <SaveIcon inactiveColor={currentTheme.activeColor} hoverColor="#ffff00" />
          </button>
        </div>
      </div>

      {/* 테마 설정 섹션 */}
      <div className={settingsCardClass}>
        <h2 className={styles.sectionTitle} style={{ margin: '0 0 15px 0' }}>
          {TEXT.themeLabel[lang]}
        </h2>

        <div className={searchStyles.searchForm}>
          <select
            className={styles.languageSelect}
            value={selectedTheme}
            onChange={handleThemeChange}
            disabled
            style={{ border: `1px dashed ${isDosTheme ? '#00ff00' : '#ccc'} !important`, opacity: 0.5, cursor: 'not-allowed' }}
          >
            {Object.values(THEME_TYPES).map(theme => (
              <option key={theme} value={theme}>
                {THEME_LABELS[theme][lang]}
              </option>
            ))}
          </select>

          <button
            onClick={handleSave}
            disabled={true}
            className={styles.saveButton}
            title={TEXT.save[lang]}
            style={{ border: 'none' }}
          >
            <SaveIcon inactiveColor={currentTheme.activeColor} hoverColor="#ffff00" />
          </button>
        </div>
      </div>

      {/* Google Calendar 연동 섹션 - admin만 표시 */}
      <SafetyLink />
      {userInfo?.level === 'admin' && <GoogleCalendarSync />}

      {/* 앱 설치 안내 */}
      <div className={settingsCardClass}>
        <h2 className={styles.sectionTitle} style={{ margin: '0 0 8px 0' }}>
          {installText.settingsTitle}
        </h2>
        <p className={styles.installDescription}>{installText.settingsDescription}</p>
        <button
          type="button"
          className={styles.installGuideButton}
          onClick={() => navigate(PWA_INSTALL_ROUTE)}
        >
          {installText.openGuide}
        </button>
      </div>

      {/* 현재 설정 정보 */}
      <div className={settingsCardClass}>
        <h3 className={styles.sectionTitle} style={{ margin: '0 0 10px 0', fontSize: '16px' }}>
          {TEXT.currentSettings[lang]}
        </h3>
        <div className={styles.infoText} style={{ fontSize: '14px' }}>
          <div>{TEXT.languageLabel[lang]}: {selectedLanguage === 'ko' ? '한국어' : selectedLanguage === 'en' ? 'English' : 'Français'}</div>
          <div>{TEXT.themeLabel[lang]}: {THEME_LABELS[selectedTheme][lang]}</div>
          <div>{TEXT.userLabel[lang]}: {userInfo?.user_name || TEXT.unknown[lang]}</div>
          <div>{TEXT.levelLabel[lang]}: {userInfo?.level || TEXT.unknown[lang]}</div>
        </div>
      </div>

      {/* 하단 여백 */}
      <div style={{ height: '50px' }}></div>
    </div>
  );
} 
